import type { CandidateSectionStatus, CandidateStatus, GradingStatus, LeaderboardResponse, StudentDashboardStats } from '@/lib/types'
import { isShowableBand } from './band-display'

/**
 * The server's `grading_error_code` for a sitting where the student submitted nothing (`views/grading.py`). Defined here, not in
 * `student-status.ts`, so this module stays free of interface-language code: the exam runner reaches it through `lib/api.ts`.
 */
export const NOT_ATTEMPTED_ERROR_CODE = 'result_not_attempted'

/** The slice of a section score this module reads or clears. Structural, so the domain imports no feature type. */
interface SectionLike {
  band?: number | null
  score?: number | null
  provisional_band?: number | null
  not_attempted?: boolean
  candidate_status?: CandidateSectionStatus
  task_bands?: Record<string, number | null>
  assessment_status?: GradingStatus
  grading_status?: GradingStatus
}

/** The slice of a result row or review summary this module reads or clears. */
interface ResultLike {
  status?: 'completed' | 'abandoned'
  timing_mode?: 'timed' | 'untimed'
  grading_status?: GradingStatus
  candidate_status?: CandidateStatus
  grading_error_code?: string | null
  error_code?: string | null
  score_publication_status?: string | null
  section_scores?: object
}

/**
 * The candidate view: what a STUDENT payload may carry once it leaves the API client (owner rule 1, 17 Oct 2026).
 *
 * A student never sees Band 0, "failed" or "unassessable". `student-status.ts` already folds those states into
 * Processing or Not attempted wherever a result is drawn; this module is the second wall, applied where student payloads
 * are fetched, so a surface that reads a raw field directly (a chart, a dashboard tile, a future component) still cannot
 * print a band that is not a band or an internal failure state. It works with or without the server's own
 * `candidate_status` (an API that has not deployed it yet, or a cached payload), and it never invents a number: a value
 * that is not a showable band becomes `null`, nothing else changes.
 *
 * Admin payloads do NOT pass through here: staff keep the real status and the real codes.
 */

const SKILLS = ['listening', 'reading', 'writing', 'speaking'] as const

/** A number that is a band the IELTS scale could name. Zero, negatives and NaN are not. */
function bandOrNull(value: unknown): number | null | undefined {
  if (value === undefined) return undefined
  return isShowableBand(value) ? value : null
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
}

function numeric(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null
}

/**
 * One section of `section_scores`. A stored band at or below zero is cleared; with no recorded points behind it (the
 * IELTS meaning of a 0: nothing was attempted) the section is also marked not attempted so every surface says it in words.
 */
export function projectSectionScore<T extends SectionLike>(score: T): T {
  const next: Record<string, unknown> = { ...(score as Record<string, unknown>) }
  const stored = numeric(score.band)
  if (stored !== null && stored <= 0) {
    next.band = null
    const points = numeric(score.score)
    const attempted = points !== null && points > 0
    if (!attempted && score.not_attempted !== true) {
      next.not_attempted = true
      if (next.candidate_status === undefined) next.candidate_status = 'not_attempted'
    }
  }
  if ('provisional_band' in score) next.provisional_band = bandOrNull(score.provisional_band) ?? null
  if (isRecord(score.task_bands)) {
    next.task_bands = Object.fromEntries(Object.entries(score.task_bands).map(([key, value]) => [key, isShowableBand(value) ? value : null]))
  }
  // A failed or unassessable section is Processing to a student.
  const lifecycle = score.assessment_status ?? score.grading_status
  if (lifecycle === 'failed' || lifecycle === 'unassessable' || lifecycle === 'processing') {
    if (next.candidate_status === undefined && next.not_attempted !== true) next.candidate_status = 'processing'
    if ('assessment_status' in next) next.assessment_status = 'processing'
    if ('grading_status' in next) next.grading_status = 'processing'
  }
  return next as T
}

/** `section_scores` of a result: every skill projected, and an overall band that is a band or null (never 0). */
export function projectSectionScores<T extends Record<string, unknown>>(scores: T): T {
  const next: Record<string, unknown> = { ...scores }
  for (const skill of SKILLS) {
    const value = scores[skill]
    if (isRecord(value)) next[skill] = projectSectionScore(value)
  }
  if ('overall_band' in scores) next.overall_band = bandOrNull(scores.overall_band) ?? null
  return next as T
}

/**
 * The student-facing lifecycle of a result row or summary: a failed or unassessable grading reads `pending` (the web and
 * older clients both treat that as "being marked"), except an abandoned attempt, which is not a grading at all.
 */
function candidateLifecycle<S extends string | undefined>(status: S, abandoned: boolean): S | 'pending' {
  if (abandoned) return status
  return status === 'failed' || status === 'unassessable' || status === 'processing' ? 'pending' : status
}

/** A row of `GET /tests/results/`, or the summary of a review. Internal codes are dropped; `candidate_status` is filled. */
export function projectCandidateResult<T extends ResultLike>(row: T): T {
  const abandoned = row.status === 'abandoned'
  const code = 'grading_error_code' in row ? row.grading_error_code : 'error_code' in row ? row.error_code : null
  const notAttempted = row.grading_status === 'unassessable' && code === NOT_ATTEMPTED_ERROR_CODE
  const next: Record<string, unknown> = { ...(row as Record<string, unknown>) }
  if (next.candidate_status === undefined) {
    if (abandoned) next.candidate_status = 'ended'
    else if (row.timing_mode === 'untimed') next.candidate_status = 'untimed'
    else if (notAttempted) next.candidate_status = 'not_attempted'
    else if (row.grading_status === 'succeeded' || row.grading_status === 'completed') next.candidate_status = row.score_publication_status === 'published' ? 'published' : 'processing'
    else next.candidate_status = 'processing'
  }
  if (row.grading_status !== undefined && !notAttempted) next.grading_status = candidateLifecycle(row.grading_status, abandoned)
  // The error code is only ever read to tell "nothing was submitted" from "not marked yet": keep that one, drop the rest.
  if (!notAttempted) {
    if ('grading_error_code' in next) next.grading_error_code = null
    if ('error_code' in next) next.error_code = null
  }
  if (isRecord(row.section_scores)) next.section_scores = projectSectionScores(row.section_scores as Record<string, unknown>)
  return next as T
}

export function projectResultList<T extends { results: ResultLike[] }>(response: T): T {
  if (!response || !Array.isArray(response.results)) return response
  return { ...response, results: response.results.map((row) => projectCandidateResult(row)) }
}

/** `GET /tests/results/<id>/review/`: the summary and the per-section scores. Answers are left as the server sent them. */
export function projectCandidateReview<T extends { result_summary: ResultLike; grading_status?: GradingStatus; assessment_status?: GradingStatus }>(data: T): T {
  if (!data || !isRecord(data.result_summary)) return data
  const abandoned = data.result_summary.status === 'abandoned'
  const summary = projectCandidateResult(data.result_summary)
  const next: T = { ...data, result_summary: summary }
  if (data.grading_status !== undefined && summary.candidate_status !== 'not_attempted') next.grading_status = candidateLifecycle(data.grading_status, abandoned)
  if (data.assessment_status !== undefined && summary.candidate_status !== 'not_attempted') next.assessment_status = candidateLifecycle(data.assessment_status, abandoned)
  return next
}

/** Dashboard figures: a section band that is not a band is `null`; the average is only ever a showable band or 0 for "none". */
export function projectDashboardStats(stats: StudentDashboardStats): StudentDashboardStats {
  if (!stats) return stats
  const clean = (bands: StudentDashboardStats['latestSectionBands']) => bands
    ? (Object.fromEntries(Object.entries(bands).map(([skill, value]) => [skill, isShowableBand(value) ? value : null])) as NonNullable<StudentDashboardStats['latestSectionBands']>)
    : bands
  return { ...stats, latestSectionBands: clean(stats.latestSectionBands), averageSectionBands: clean(stats.averageSectionBands) }
}

/** Leaderboard: an average that is not a band is `null` ("No band yet"), and progression keeps only real bands. */
export function projectLeaderboard(response: LeaderboardResponse): LeaderboardResponse {
  if (!response) return response
  const entry = <E extends LeaderboardResponse['leaderboard'][number] | null>(value: E): E => (value ? { ...value, avg_band: isShowableBand(value.avg_band) ? value.avg_band : null } : value)
  return {
    ...response,
    leaderboard: Array.isArray(response.leaderboard) ? response.leaderboard.map((row) => entry(row)) : response.leaderboard,
    current_user: entry(response.current_user),
    progression: Array.isArray(response.progression) ? response.progression.filter((point) => isShowableBand(point.band)) : response.progression,
  }
}
